import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Chip, CircularProgress, Divider, InputAdornment,
  MenuItem, Pagination, Paper, Select, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import SearchIcon         from '@mui/icons-material/Search';
import TrendingUpIcon     from '@mui/icons-material/TrendingUp';
import MailOutlineIcon    from '@mui/icons-material/MailOutline';
import SwapCallsIcon      from '@mui/icons-material/SwapCalls';
import WarningAmberIcon   from '@mui/icons-material/WarningAmber';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon   from '@mui/icons-material/ArrowForward';
import { intelligenceApi } from '../../api/intelligenceApi';

const GREEN     = '#2db838';
const GREEN_DRK = '#1e9128';

// ── Helpers ────────────────────────────────────────────────────────────────
const GENERIC_DOMAINS = new Set([
  'gmail', 'yahoo', 'hotmail', 'outlook', 'icloud', 'aol',
  'protonmail', 'mail', 'live', 'msn', 'me', 'ymail', 'inbox',
]);

function orgTag(email) {
  if (!email) return null;
  const domain = email.split('@')[1] || '';
  const part = domain.split('.')[0].toUpperCase();
  return GENERIC_DOMAINS.has(part.toLowerCase()) ? null : part;
}

function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color = '#111827', accent }) {
  return (
    <Paper elevation={0} sx={{
      flex: 1, minWidth: 160,
      p: 2.5, border: '1px solid #e5e7eb', borderRadius: 2,
      borderTop: `3px solid ${accent || color}`,
    }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Box sx={{ color: accent || '#9ca3af' }}>{icon}</Box>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {label}
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>{value}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: '#9ca3af', mt: 0.5 }}>{sub}</Typography>
      )}
    </Paper>
  );
}

// ── Funnel bar ─────────────────────────────────────────────────────────────
function FunnelBar({ funnel }) {
  const stages = [
    { label: 'In System',       key: 'total_in_system',  color: '#e5e7eb', text: '#6b7280' },
    { label: 'Have Email',      key: 'have_email',       color: '#d1fae5', text: '#059669' },
    { label: 'Emailed Us',      key: 'contacted',        color: '#86efac', text: '#16a34a' },
    { label: 'Quoted',          key: 'quote_requested',  color: GREEN,     text: '#fff'    },
    { label: 'Prepared',        key: 'quote_prepared',   color: '#1e9128', text: '#fff'    },
    { label: 'Converted',       key: 'converted',        color: '#14532d', text: '#fff'    },
  ];

  const total = funnel?.total_in_system || 1;

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 3 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', mb: 2 }}>
        Sales Funnel — Drop-off by Stage
      </Typography>

      {/* Proportional bars */}
      <Stack direction="row" alignItems="stretch" sx={{ height: 48, borderRadius: 1.5, overflow: 'hidden', mb: 1.5 }}>
        {stages.map((s, i) => {
          const count = funnel?.[s.key] ?? 0;
          const width = Math.max(4, pct(count, total));
          return (
            <Tooltip key={s.key} title={`${s.label}: ${count.toLocaleString()} contacts`} arrow>
              <Box
                sx={{
                  width: `${width}%`, bgcolor: s.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRight: i < stages.length - 1 ? '2px solid #fff' : 'none',
                  cursor: 'default', transition: 'opacity 0.2s',
                  '&:hover': { opacity: 0.85 },
                }}
              >
                {width > 8 && (
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: s.text, textAlign: 'center', px: 0.5 }}>
                    {pct(count, total)}%
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Stack>

      {/* Legend */}
      <Stack direction="row" flexWrap="wrap" gap={1.5}>
        {stages.map((s) => {
          const count = funnel?.[s.key] ?? 0;
          const dropPct = s.key !== 'total_in_system'
            ? pct(total - count, total)
            : null;
          return (
            <Stack key={s.key} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: s.color, border: '1px solid #e5e7eb', flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#374151' }}>
                  {s.label}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  {count.toLocaleString()}
                  {dropPct !== null && dropPct > 0 && (
                    <span style={{ color: '#ef4444', marginLeft: 4 }}>−{dropPct}%</span>
                  )}
                </Typography>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

// ── Attribution chips ──────────────────────────────────────────────────────
function AttributionRow({ attribution }) {
  const total = (attribution?.high ?? 0) + (attribution?.probable ?? 0) + (attribution?.unlinked ?? 0);
  const items = [
    {
      label: 'High confidence',
      count: attribution?.high ?? 0,
      color: GREEN, bg: '#f0fdf4', border: '#bbf7d0',
      tip: 'Email → conversation → inquiry chain fully linked',
    },
    {
      label: 'Probable match',
      count: attribution?.probable ?? 0,
      color: '#b45309', bg: '#fffbeb', border: '#fde68a',
      tip: 'Linked by contact ID + time window — no inquiry chain',
    },
    {
      label: 'Unlinked',
      count: attribution?.unlinked ?? 0,
      color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb',
      tip: 'No email history found — cold contact only',
    },
  ];

  return (
    <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5, mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>
          Attribution Confidence
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
          How reliable is the funnel data?
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1.5} flexWrap="wrap" gap={1}>
        {items.map((item) => (
          <Tooltip key={item.label} title={item.tip} arrow placement="top">
            <Box sx={{
              px: 2, py: 1, borderRadius: 2,
              bgcolor: item.bg, border: `1px solid ${item.border}`,
              cursor: 'default',
            }}>
              <Typography sx={{ fontSize: 18, fontWeight: 800, color: item.color }}>
                {item.count.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: item.color }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>
                {total > 0 ? pct(item.count, total) : 0}% of contacts
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Stack>
    </Paper>
  );
}

// ── Shut-out score dot ─────────────────────────────────────────────────────
function ShutOutDot({ score }) {
  const color = score >= 70 ? '#ef4444' : score >= 40 ? '#f59e0b' : '#22c55e';
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color }}>{score}</Typography>
    </Stack>
  );
}

// ── Main component ─────────────────────────────────────────────────────────
export default function SalesFunnelInsights({ onContactClick }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Table state
  const [search,         setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort,           setSort]           = useState('quotations_requested');
  const [dir,            setDir]            = useState('desc');
  const [page,           setPage]           = useState(1);
  const [minQuotes,      setMinQuotes]      = useState('0');
  const [attrFilter,     setAttrFilter]     = useState('');
  const [flaggedOnly,    setFlaggedOnly]    = useState(false);

  // Debounce search input — only the debounced value drives the fetch
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  // Single fetch effect — fires on mount and whenever any param changes.
  // Never clears existing data before the response arrives so pagination feels
  // instant rather than blanking the screen between pages.
  useEffect(() => {
    setError(null);
    setLoading(true);
    intelligenceApi.getSalesFunnels({
      sort, dir, page, search: debouncedSearch,
      min_quotes: minQuotes,
      attribution: attrFilter,
      flagged: flaggedOnly ? '1' : '',
    })
      .then(setData)
      .catch((err) => setError(err?.response?.data?.message || 'Failed to load funnel data.'))
      .finally(() => setLoading(false));
  }, [sort, dir, page, debouncedSearch, minQuotes, attrFilter, flaggedOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (col) => {
    if (sort === col) {
      setDir((d) => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(col);
      setDir('desc');
    }
    setPage(1);
  };

  const { summary, funnel, attribution, contacts } = data ?? {};

  return (
    <Box>
      {/* ── Section header ── */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2.5}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>
            Sales Funnel Intelligence
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#6b7280' }}>
            Live data from {summary?.total_contacts?.toLocaleString() ?? '—'} contact profiles ·
            refreshes weekly every Monday at 03:00
          </Typography>
        </Box>
        {loading && <CircularProgress size={20} sx={{ color: GREEN }} />}
      </Stack>

      {/* ── Stat cards ── */}
      <Stack direction="row" spacing={2} mb={3} flexWrap="wrap" gap={2}>
        <StatCard
          icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
          label="Quote Requests"
          value={(summary?.total_quote_requests ?? 0).toLocaleString()}
          sub={`from ${(funnel?.quote_requested ?? 0).toLocaleString()} contacts`}
          accent={GREEN}
        />
        <StatCard
          icon={<SwapCallsIcon sx={{ fontSize: 18 }} />}
          label="Conversions"
          value={(summary?.total_conversions ?? 0).toLocaleString()}
          sub={summary?.total_quote_requests > 0
            ? `${pct(summary.total_conversions, summary.total_quote_requests)}% of quotes`
            : 'No conversions yet'}
          accent="#1565c0"
        />
        <StatCard
          icon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
          label="Campaign Sends"
          value={(summary?.total_campaign_sends ?? 0).toLocaleString()}
          sub={`${(summary?.total_campaign_responses ?? 0).toLocaleString()} replies received`}
          accent="#7c3aed"
        />
        <StatCard
          icon={<WarningAmberIcon sx={{ fontSize: 18 }} />}
          label="Flagged Contacts"
          value={(summary?.flagged_count ?? 0).toLocaleString()}
          sub="need alternate outreach"
          accent={summary?.flagged_count > 0 ? '#ef4444' : '#9ca3af'}
          color={summary?.flagged_count > 0 ? '#ef4444' : '#111827'}
        />
      </Stack>

      {/* ── Funnel bar ── */}
      <FunnelBar funnel={funnel} />

      {/* ── Attribution ── */}
      <AttributionRow attribution={attribution} />

      <Divider sx={{ mb: 3 }} />

      {/* ── Table header + filters ── */}
      <Stack direction="row" alignItems="center" spacing={2} mb={2} flexWrap="wrap" gap={1.5}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#111827', mr: 'auto' }}>
          Top Contacts by Signal Quality
        </Typography>

        <TextField
          size="small"
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 220 }}
        />

        <Select
          size="small"
          value={minQuotes}
          onChange={(e) => { setMinQuotes(e.target.value); setPage(1); }}
          sx={{ minWidth: 145, fontSize: 13 }}
        >
          <MenuItem value="0">All contacts</MenuItem>
          <MenuItem value="1">≥ 1 quote request</MenuItem>
          <MenuItem value="5">≥ 5 quote requests</MenuItem>
          <MenuItem value="20">≥ 20 quote requests</MenuItem>
        </Select>

        <Select
          size="small"
          value={attrFilter}
          onChange={(e) => { setAttrFilter(e.target.value); setPage(1); }}
          sx={{ minWidth: 145, fontSize: 13 }}
        >
          <MenuItem value="">All attribution</MenuItem>
          <MenuItem value="high">High confidence</MenuItem>
          <MenuItem value="probable">Probable</MenuItem>
          <MenuItem value="unlinked">Unlinked</MenuItem>
        </Select>

        <Chip
          label="Flagged only"
          clickable
          variant={flaggedOnly ? 'filled' : 'outlined'}
          color={flaggedOnly ? 'error' : 'default'}
          size="small"
          onClick={() => { setFlaggedOnly((v) => !v); setPage(1); }}
        />
      </Stack>

      {/* ── Table ── */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2, opacity: loading ? 0.55 : 1, transition: 'opacity 0.15s' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#fafafa' }}>
              <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Contact</TableCell>
              {[
                { key: 'quotations_requested', label: 'Quote Requests' },
                { key: 'total_inbound_emails', label: 'Emails In' },
                { key: 'total_outbound_emails', label: 'Emails Out' },
                { key: 'response_rate_pct',   label: 'Response %' },
                { key: 'conversions',         label: 'Conversions' },
                { key: 'campaign_sends',      label: 'Campaigns' },
                { key: 'shut_out_score',      label: 'Shut-out' },
              ].map(({ key, label }) => (
                <TableCell key={key} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={sort === key}
                    direction={sort === key ? dir : 'desc'}
                    onClick={() => handleSort(key)}
                  >
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={{ fontWeight: 700 }}>Attribution</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Last Reply</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {contacts?.data?.map((row) => (
              <TableRow
                key={row.contact_id}
                hover
                sx={{ cursor: onContactClick ? 'pointer' : 'default' }}
                onClick={() => onContactClick?.(row.contact_id)}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 200 }}>
                    {row.name || '—'}
                    {orgTag(row.email) ? (
                      <Typography component="span" variant="body2" sx={{ color: '#6b7280', fontWeight: 400, ml: 0.5 }}>
                        ({orgTag(row.email)})
                      </Typography>
                    ) : null}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                    {row.email}
                  </Typography>
                  {!!row.flagged_for_alternate_contact && (
                    <Chip
                      label="⚠ Flagged"
                      size="small"
                      color="error"
                      variant="outlined"
                      sx={{ fontSize: 9, height: 16, mt: 0.25 }}
                    />
                  )}
                </TableCell>

                <TableCell>
                  <Typography variant="body2" fontWeight={row.quotations_requested > 0 ? 700 : 400}
                    sx={{ color: row.quotations_requested > 0 ? GREEN_DRK : '#9ca3af' }}>
                    {row.quotations_requested}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{row.total_inbound_emails}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="body2">{row.total_outbound_emails}</Typography>
                </TableCell>

                <TableCell>
                  {row.total_outbound_emails > 0 ? (
                    <Typography variant="body2" fontWeight={600}
                      sx={{ color: row.response_rate_pct >= 50 ? GREEN : row.response_rate_pct >= 20 ? '#f59e0b' : '#9ca3af' }}>
                      {row.response_rate_pct}%
                    </Typography>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#d1d5db' }}>—</Typography>
                  )}
                </TableCell>

                <TableCell>
                  {row.conversions > 0 ? (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: GREEN }} />
                      <Typography variant="body2" fontWeight={700} sx={{ color: GREEN }}>
                        {row.conversions}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#d1d5db' }}>—</Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2">{row.campaign_sends}</Typography>
                    {row.campaign_responses > 0 && (
                      <Typography variant="caption" sx={{ color: GREEN, fontWeight: 700 }}>
                        +{row.campaign_responses}
                      </Typography>
                    )}
                    {row.campaign_non_response_streak >= 2 && (
                      <Chip
                        label={`${row.campaign_non_response_streak}×`}
                        size="small"
                        sx={{ fontSize: 9, height: 16, bgcolor: '#fef2f2', color: '#ef4444', fontWeight: 700 }}
                      />
                    )}
                  </Stack>
                </TableCell>

                <TableCell>
                  <ShutOutDot score={row.shut_out_score} />
                </TableCell>

                <TableCell>
                  <Chip
                    label={row.attribution_confidence}
                    size="small"
                    sx={{
                      fontSize: 10, height: 20, fontWeight: 700,
                      bgcolor: row.attribution_confidence === 'high'     ? '#f0fdf4'
                             : row.attribution_confidence === 'probable' ? '#fffbeb' : '#f9fafb',
                      color:   row.attribution_confidence === 'high'     ? GREEN
                             : row.attribution_confidence === 'probable' ? '#b45309' : '#6b7280',
                    }}
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {fmtDate(row.last_responded_at)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}

            {!loading && error && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="error" variant="body2">{error}</Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && contacts?.data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="body2">
                    No contacts match your filters.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Pagination ── */}
      {contacts?.last_page > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Pagination
            count={contacts.last_page}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
}
